import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ProductionPage } from './pages/production/ProductionPage';
import { DashboardPage } from './pages/dashboard/DashboardPage.js';
import { TrialHomePage } from './pages/trial/TrialHomePage';
import { TrialSetupPage } from './pages/trial/TrialSetupPage';
import { TrialMobilePage } from './pages/trial/TrialMobilePage';
import { TrialLivePage } from './pages/demo/TrialLivePage.js';
import { Profile } from './pages/settings/profile/Profile';
import { CoreLayout } from './components/layout/CoreLayout.js';
import { TrialLayout } from './components/layout/TrialLayout.js';
import { SalesPage } from './pages/sales/SalesPage.js';
import { ProtectedRoute } from './components/routes/ProtectedRoute.js';
import { LogLayout } from './components/layout/LogLayout.js';
import { ResetPasswordPage } from './pages/reset-password/ResetPasswordPage.js';
import Issue from './assets/images/issue.jpg';
import { OperatorPage } from './pages/operator/OperatorPage.js';
import { OperatorWorkstationPage } from './pages/operator/OperatorWorkstationPage.js';
import { isMobileDevice } from './helpers/common/isMobileDevice.js';
import { Overview } from './pages/dashboard/Overview.js';
import { Workshop } from './pages/dashboard/Workshop.js';
import { Workstation } from './pages/dashboard/Workstation.js';
import { SetupPage } from './pages/setup/SetupPage.js';
import { SpecificationPage } from './pages/setup/SpecificationPage.js';
import { CalibrationPage } from './pages/calibration/CalibrationPage.js';
import { ErgonomicsPage } from './pages/ergonomics/ErgonomicsPage';
import { ProcessesPage } from "./pages/processes/ProcessesPage";
import { ProcessPage } from "./pages/processes/ProcessPage";
import { NewProcessPage } from "./pages/processes/NewProcessPage";
import { ProcessLayout } from "./components/layout/ProcessLayout";
import { TAGLivePage } from "./pages/live-interface/TAGLivePage";
import { TAGUSLivePage } from "./pages/live-interface/TAGUSLivePage";
import { LCALivePage } from "./pages/live-interface/LCALivePage";
import { HermesLivePage } from "./pages/live-interface/HermesLivePage";
import { LISILivePage } from "./pages/live-interface/LISILivePage";
import { HermesUSLivePage } from "./pages/live-interface/HermesUSLivePage";
import { RenaultLivePage } from "./pages/live-interface/RenaultLivePage";
import { EDFLivePage } from "./pages/live-interface/EDFLivePage";
import { QualityPage } from "./pages/quality/QualityPage";
import { SnapshotsGalleryPage } from "./pages/gallery/snapshots/SnapshotsGalleryPage";
import { EventScoresGalleryPage } from "./pages/gallery/event-scores/EventScoresGalleryPage";
import { DevicesSettingsPage } from "./pages/settings/devices/DevicesSettingsPage";
import { DiorLivePage } from "./pages/live-interface/DiorLivePage";
import { AuditPage } from "./pages/audit/AuditPage";
import { AuditSummaryPage } from "./pages/audit/AuditSummaryPage";

export const App = () => {
  const location = useLocation();

  const mobilePages = ['/demo/stream', '/calibration', '/quality', '/audit', '/settings/devices'];
  const isTrialMobilePage = mobilePages.some((page) => location.pathname.includes(page));

  // if (isMobileDevice() && !isTrialMobilePage) return <ResponsiveFallbackComponent />;

  const LivePage = {
    'EDF': EDFLivePage,
    'Hermes': HermesLivePage,
    'HermesUS': HermesUSLivePage,
    'LCA': LCALivePage,
    'LISI': LISILivePage,
    'Renault': RenaultLivePage,
    'TAG': TAGLivePage,
    'TAG-US': TAGUSLivePage,
    'Dior': DiorLivePage,
  }[process.env.REACT_APP_CUSTOMER_NAME];

  const GalleryPage = {
    'snapshots': SnapshotsGalleryPage,
    'event_scores': EventScoresGalleryPage
  }[process.env.REACT_APP_GALLERY_MODE]

  return (
    <Routes>
      <Route element={<CoreLayout />}>
        <Route path='/' element={<Navigate to={process.env.REACT_APP_DEFAULT_PAGE} replace />} />

        {/* Trial pages. */}
        <Route element={<TrialLayout />}>
          <Route path='/demo/setup' element={<TrialSetupPage />} />
          <Route path='/demo' element={<TrialHomePage />} />
          <Route path='/demo/stream/:desktopId' element={<TrialMobilePage />} />
          <Route path='/demo/live' element={<TrialLivePage />} />
        </Route>

        {/* Page requiring connection to the engine. */}
        <Route element={<LogLayout />}>
          <Route path='/production' element={<ProductionPage />} />
          <Route path='/production/:subtype' element={<ProductionPage />} />
          <Route path='/register/:organization_token' element={<ProductionPage />} />
          <Route path='/reset-password/:token' element={<ResetPasswordPage />} />
        </Route>
        {/* Setup pages. */}
        <Route path='/setup' element={<SetupPage />} />
        <Route path='/setup/:subtype' element={<SetupPage />} />
        <Route path='/specification/:deviceId' element={<SpecificationPage />} />

        {/* Dashboard pages. */}
        <Route path='/dashboard' element={<DashboardPage />}>
          <Route path='overview' element={<Overview />} />
          <Route path='workshop' element={<Workshop />} />
          <Route path='workstation' element={<Workstation />} />
          <Route path='workstation/:workstationId/:workshopId/:startDate/:endDate' element={<Workstation />} />
        </Route>

        {/* Commons pages. */}
        <Route path='/settings' element={<Outlet />}>
          <Route path='profile' element={<Profile />} />
          <Route path='devices/:deviceToken1/:deviceToken2' element={<DevicesSettingsPage />} />
        </Route>

        {/* Protected and sensitive pages */}
        <Route
          path='/backoffice'
          element={
            <ProtectedRoute requiredRole='ROLE_ROOT'>
              <SalesPage />
            </ProtectedRoute>
          }
        />

        {/* IHM operator pages */}
        <Route path='/operator' element={<OperatorPage />} />
        <Route path='/operator/:workstationId' element={<OperatorWorkstationPage />} />

        {/* Processes pages. */}
        <Route element={<ProcessLayout />}>
          <Route path='/processes' element={<ProcessesPage />} />
          <Route path='/processes/new' element={<NewProcessPage />} />
          <Route path='/processes/:patternId' element={<ProcessPage />} />

          {/* Operator pages (V2) */}
          <Route path='/interface/live' element={<LivePage />} />
        </Route>

        {/* Quality control pages */}
        <Route path='/gallery' element={<GalleryPage />} />

        {/* Audit control pages */}
        <Route path='/audit' element={<AuditPage />} />
        <Route path='/audit/summary' element={<AuditSummaryPage />} />

        <Route path='*' element={<Navigate to='/production' replace />} />

        {/* Ergonomics pages */}
        <Route path='/ergonomics' element={<ErgonomicsPage />} />

        {/* Camera calibration page */}
        <Route path='/calibration/:deviceToken1/:deviceToken2' element={<CalibrationPage />} />

        {/* Quality control page */}
        <Route path='/quality' element={<QualityPage />} />
      </Route>
    </Routes>
  );
};

const ResponsiveFallbackComponent = () => {
  return (
    <div className='fixed top-0 left-0 flex flex-col items-center justify-center w-full h-full px-4 md:flex-row'>
      <img src={Issue} className='w-2/3 h-auto md:w-2/4' alt='issue, this website is not optimized for mobile' />
      <div className='max-w-md p-6 text-center bg-white rounded-lg md:text-left'>
        <h1 className='mb-2 text-2xl font-bold'>Oops!</h1>
        <p className='mb-2'>This site is not optimized for mobile devices.</p>
        <p>Please open it on a desktop or laptop computer for a better experience.</p>
        <div className='mt-4'>
          <a
            href='https://www.linkedin.com/company/perception-manufacturing'
            rel='noopener noreferrer'
            className='px-4 py-2 font-bold text-white rounded bg-perception-blue hover:bg-blue-700'
          >
            Contact us
          </a>
        </div>
      </div>
    </div>
  );
};
